"use client";

import { useId, useState, useRef, type ReactNode, type KeyboardEvent } from "react";

interface ServiceChipProps {
  /** Pre-rendered icon element (lucide icons are passed in from the server component). */
  icon: ReactNode;
  name: string;
  /** Short description of the attack used against this service in the labs. */
  attack: string;
}

const TOOLTIP_WIDTH = 256;

/**
 * A service pill in the attack-path diagram. Hovering, focusing or tapping it
 * reveals a tooltip describing how the service is attacked in the labs. The
 * tooltip flips to right-alignment when it would overflow the viewport.
 */
export function ServiceChip({ icon, name, attack }: ServiceChipProps) {
  const id = useId();
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);

  const show = () => {
    const el = ref.current;
    if (el) {
      const { left } = el.getBoundingClientRect();
      setAlignRight(left + TOOLTIP_WIDTH > window.innerWidth - 16);
    }
    setOpen(true);
  };
  const hide = () => setOpen(false);

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Escape") hide();
  };

  return (
    <button
      ref={ref}
      type="button"
      aria-describedby={id}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onClick={() => (open ? hide() : show())}
      onKeyDown={onKeyDown}
      className={`relative flex min-h-11 items-center gap-1.5 rounded-md border border-cb-border bg-cb-bg px-2.5 py-1.5 text-left transition-colors hover:border-cb-violet/40 hover:bg-cb-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cb-violet/60 active:bg-cb-surface-2 sm:min-h-0 ${
        open ? "z-30 border-cb-violet/40" : "z-0"
      }`}
    >
      {icon}
      <span className="text-xs font-medium text-cb-muted">{name}</span>
      <span
        role="tooltip"
        id={id}
        className={`pointer-events-none absolute bottom-full z-30 mb-2 w-64 rounded-lg border border-cb-violet/30 bg-cb-bg p-3 text-xs leading-relaxed text-cb-muted shadow-xl shadow-black/40 transition-opacity duration-150 ${
          open ? "visible opacity-100" : "invisible opacity-0"
        } ${alignRight ? "right-0" : "left-0"}`}
      >
        <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-cb-violet-text">
          {name}
        </span>
        {attack}
      </span>
    </button>
  );
}
